import * as React from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import { useAnalytics } from "@/providers/analytics";
import { useCurrentOrgId } from "@/providers/auth/hooks";
import {
  FormPageHeading,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { FilePicker } from "@/shared/components/FilePicker";
import {
  ResourceFormActionsRow,
  ResourceFormNotice,
  ResourceFormStatus,
} from "@/shared/components/ResourceForm";
import { sessionCompositionEntryReason } from "@/shared/components/sessions/sessionCompositionEntryReason";
import { type FileRecord } from "@/shared/domain/files";
import {
  getSessionCompositionCounts,
  type SessionCompositionEntry,
  type SessionCreationResult,
} from "@/shared/domain/sessions";
import {
  ButtonLink,
  Field,
  FormSection,
  RobotoButton,
  Spinner,
  TextInput,
} from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation/Goto";
import { type CreateSessionLocationState } from "@/shared/navigation/types";

import styles from "./CreateSessionPage.module.css";
import { DevicesSection } from "./DevicesSection";
import { FilesSection } from "./FilesSection";
import { SessionCustomFieldsSection } from "./SessionCustomFieldsSection";
import {
  useCreateSessionForm,
  type CreateSessionForm,
} from "./useCreateSessionForm";

/** Above this many, a result names its files behind a disclosure. */
const MAX_INLINE_FILES = 3;

/**
 * Full-page create-session form at `/sessions/create`. Reachable from the
 * top-level Sessions page, the Sessions tab in dataset details, and a
 * dataset's file-browser selection menu — dataset-scoped entry points seed
 * the file selection via the `dataset_id` query param and navigation state.
 */
export function CreateSessionPage() {
  const orgId = useCurrentOrgId();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const [searchParams] = useSearchParams();
  // Normalize both a missing param and the malformed empty value
  // (`?dataset_id=`) to undefined, so every downstream "dataset in scope"
  // guard agrees on one definition.
  const datasetId = searchParams.get("dataset_id") || undefined;

  const location = useLocation();
  const locationState = location.state as CreateSessionLocationState | null;

  const form = useCreateSessionForm({
    orgId,
    pageKey: location.key,
    datasetId,
    fileIds: locationState?.fileIds,
    directoryPatterns: locationState?.directoryPatterns,
  });

  const [filePickerOpen, setFilePickerOpen] = React.useState(false);
  // The files the last attempt was built from, so its result keeps naming
  // them after the user edits the selection.
  const [attemptFiles, setAttemptFiles] = React.useState<readonly FileRecord[]>(
    [],
  );

  const handleCancel = () => {
    trackEvent("CreateSessionCanceled");
    form.stop();
    // On a direct entry (deep link, refresh, new tab) there is no in-app
    // history entry behind this page — `navigate(-1)` would no-op or leave
    // the SPA — so fall back to the Sessions list.
    if (location.key === "default") {
      void navigate(Goto.route.Sessions);
    } else {
      void navigate(-1);
    }
  };

  const handleSubmit = () => {
    trackEvent("CreateSessionSubmitted", {
      datasetId,
      fileCount: form.selectedFiles.length,
    });
    // An attempt stopped before its create went out is still held; release
    // it so this click starts a new one.
    if (!form.canSubmit) {
      form.prepareNewCreation();
    }
    setAttemptFiles(form.selectedFiles);
    void form.submit();
  };

  return (
    <PageLayout
      title="Roboto - Create session"
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Sessions}>Sessions</Link>
          <span>Create session</span>
        </PageBreadcrumbs>
      }
    >
      <div className={styles.page}>
        <div className={styles.heading}>
          <FormPageHeading
            title="Create session"
            subtitle="Group related files into one session so you can analyze them together."
          />
          <DatasetSubhead datasetId={datasetId} />
        </div>

        <NameSection name={form.name} onChange={form.setName} />

        <FilesSection
          files={form.selectedFiles}
          isLoadingFiles={form.isPrefilling}
          filesLoadError={form.prefillError}
          onAddFiles={() => setFilePickerOpen(true)}
          onRemoveFiles={form.removeFiles}
        />

        <DevicesSection
          orgId={orgId}
          inferredDeviceIds={form.inferredDeviceIds}
          isInferringDevices={form.isInferringDevices}
          inferDevicesError={form.inferDevicesError}
          explicitDeviceIds={form.explicitDeviceIds}
          onAddDevice={form.addDevice}
          onRemoveDevice={form.removeDevice}
        />

        <SessionCustomFieldsSection
          orgId={orgId}
          values={form.customFieldValues}
          onChange={form.setCustomFieldValues}
        />

        <CreateSessionActions
          form={form}
          attemptFiles={attemptFiles}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      </div>

      <FilePicker
        orgId={orgId}
        open={filePickerOpen}
        onClose={() => setFilePickerOpen(false)}
        onSubmit={form.mergeFiles}
        title="Add files to session"
        activeDatasetIds={datasetId ? [datasetId] : undefined}
        existingFileIds={form.selectedFiles.map((file) => file.file_id)}
      />
    </PageLayout>
  );
}

function DatasetSubhead({ datasetId }: { datasetId: string | undefined }) {
  if (datasetId === undefined) {
    return null;
  }
  return (
    <p className={styles.muted}>
      Creating from dataset{" "}
      <Link to={Goto.createDatasetRoute(datasetId)}>{datasetId}</Link>
    </p>
  );
}

/** What the session is called; the section's title is the field's label. */
function NameSection({
  name,
  onChange,
}: {
  name: string;
  onChange: (name: string) => void;
}) {
  const titleId = React.useId();
  return (
    <FormSection
      title="Name"
      titleId={titleId}
      hint="Optional. You can rename the session later."
    >
      <Field.Root>
        <Field.Control
          aria-labelledby={titleId}
          render={<TextInput autoComplete="off" size="lg" />}
          value={name}
          // Mirrors the API's 120-character cap on session names.
          maxLength={120}
          placeholder="e.g. Morning test drive"
          onChange={(event) => onChange(event.target.value)}
        />
      </Field.Root>
    </FormSection>
  );
}

interface ActionsRowContent {
  notice?: React.ReactNode;
  progress?: React.ReactNode;
  status: React.ReactNode;
  buttons: React.ReactNode;
}

/**
 * The row the form ends on, the same one the trigger and agent forms end on,
 * and the page's one place for the creation attempt: what is left to do and
 * the submit; then the attempt's progress while it runs; then its result,
 * above the row, with the actions that recover from it in the row, until the
 * user discards the result.
 */
function CreateSessionActions({
  form,
  attemptFiles,
  onCancel,
  onSubmit,
}: {
  form: CreateSessionForm;
  attemptFiles: readonly FileRecord[];
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const navigate = useNavigate();
  let content: ActionsRowContent;
  if (form.isSubmitting) {
    content = creatingContent(form);
  } else if (form.creationResult !== undefined) {
    content = resultContent({
      form,
      result: form.creationResult,
      attemptFiles,
      onTryAgain: onSubmit,
      openSessions: () => {
        void form.refreshSessions().then(() => navigate(Goto.route.Sessions));
      },
    });
  } else {
    content = draftContent(form, onCancel, onSubmit);
  }
  return <ResourceFormActionsRow {...content} />;
}

/** Before an attempt, or after one stopped before its create request went out. */
function draftContent(
  form: CreateSessionForm,
  onCancel: () => void,
  onSubmit: () => void,
): ActionsRowContent {
  // An attempt that stopped before its create request went out, or that
  // failed outright, holds a prepared composition and no result; the user
  // may start over.
  const canStartOver =
    form.outcome !== undefined &&
    !form.isRecovering &&
    !form.isPrefilling &&
    form.prefillError === null;
  return {
    notice: hasContent(form.submitError) && (
      <ResourceFormNotice tone="error" role="alert">
        {form.submitError}
      </ResourceFormNotice>
    ),
    status: <ResourceFormStatus>{draftStatus(form)}</ResourceFormStatus>,
    buttons: (
      <>
        <RobotoButton variant="secondary" onClick={onCancel}>
          Cancel
        </RobotoButton>
        <RobotoButton
          variant="primary"
          disabled={!form.canSubmit && !canStartOver}
          onClick={onSubmit}
        >
          Create session
        </RobotoButton>
      </>
    ),
  };
}

/**
 * A failed attempt sets `submitError`; a stopped one never does, so the error
 * is what tells the two apart while either holds its prepared composition.
 */
function draftStatus(form: CreateSessionForm): string {
  if (hasContent(form.submitError)) {
    return "Try again, or change the form.";
  }
  if (form.outcome !== undefined) {
    return "Stopped. Nothing was created.";
  }
  if (form.selectedFiles.length === 0) {
    return "Add files, then create the session.";
  }
  return `Ready to create the session with ${fileCount(form.selectedFiles.length)}.`;
}

/** Covers every value React renders as nothing — not just `null` — so an
 * empty message can't paint an empty error box. */
function hasContent(node: React.ReactNode): boolean {
  return (
    node !== null &&
    node !== undefined &&
    node !== false &&
    node !== true &&
    node !== ""
  );
}

/** While the attempt runs: how many files are in, and a way to stop. */
function creatingContent(form: CreateSessionForm): ActionsRowContent {
  const total = form.outcome?.entries.length ?? 0;
  const added = form.outcome
    ? getSessionCompositionCounts(form.outcome).included
    : 0;
  return {
    progress: (
      <div
        className={styles.progress}
        role="progressbar"
        aria-label="Files added"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={added}
      >
        <div
          className={styles.progressFill}
          style={{ width: total > 0 ? `${(added / total) * 100}%` : 0 }}
        />
      </div>
    ),
    status: (
      <ResourceFormStatus>
        <Spinner aria-hidden />
        <span>
          Creating session
          {total > 0 && (
            <>
              {" "}
              · adding files{" "}
              <strong className={styles.progressCount}>
                {added} of {total}
              </strong>
            </>
          )}
        </span>
      </ResourceFormStatus>
    ),
    buttons: (
      <>
        <RobotoButton variant="ghost" onClick={form.stop}>
          Stop
        </RobotoButton>
        <RobotoButton variant="primary" loading>
          Create session
        </RobotoButton>
      </>
    ),
  };
}

/**
 * What the attempt left behind, in the user's terms, and what to do next:
 * open the session it left, check whether it is still there, look through
 * the sessions list, try again with the form as it is now, or discard the
 * result.
 */
function resultContent({
  form,
  result,
  attemptFiles,
  onTryAgain,
  openSessions,
}: {
  form: CreateSessionForm;
  result: SessionCreationResult;
  attemptFiles: readonly FileRecord[];
  onTryAgain: () => void;
  openSessions: () => void;
}): ActionsRowContent {
  const busy = form.isSubmitting || form.isRecovering;
  const discard = (
    <RobotoButton
      variant="ghost"
      disabled={busy}
      onClick={form.prepareNewCreation}
    >
      Discard
    </RobotoButton>
  );

  switch (result.kind) {
    case "completed":
      // The form opens the session itself; this shows only when the user
      // edited or stopped after the last batch was already on its way.
      return {
        status: <ResourceFormStatus>Session created.</ResourceFormStatus>,
        buttons: (
          <>
            {discard}
            <ViewSessionLink sessionId={result.sessionId} />
          </>
        ),
      };
    case "compensated": {
      const stopped = result.outcome.stopCause?.kind === "stopped";
      return {
        notice: (
          <ResourceFormNotice
            tone={stopped ? "neutral" : "error"}
            role={stopped ? "status" : "alert"}
            title={
              stopped
                ? "Stopped, so the session wasn't kept."
                : "Couldn't add every file, so the session wasn't kept."
            }
          >
            <AffectedFiles
              label="Not added"
              entries={unaddedEntries(result)}
              files={attemptFiles}
            />
            <ReconciliationNote result={result} />
          </ResourceFormNotice>
        ),
        status: (
          <ResourceFormStatus>
            Change the form if you need to, then try again.
          </ResourceFormStatus>
        ),
        buttons: (
          <>
            {discard}
            <RobotoButton
              variant="primary"
              disabled={busy}
              onClick={onTryAgain}
            >
              Try again
            </RobotoButton>
          </>
        ),
      };
    }
    case "cleanupUnknown": {
      return {
        notice: (
          <ResourceFormNotice
            tone="warning"
            role="alert"
            title={cleanupUnknownTitle(result)}
          >
            <AffectedFiles
              label="Not confirmed as added"
              entries={unaddedEntries(result)}
              files={attemptFiles}
            />
            <ReconciliationNote result={result} />
            {result.survivingSession && (
              <p className={styles.resultLine}>
                Open the session to check it before creating another.
              </p>
            )}
          </ResourceFormNotice>
        ),
        status: (
          <ResourceFormStatus>
            Check the session, or discard these results to start over.
          </ResourceFormStatus>
        ),
        buttons: (
          <>
            {discard}
            <RobotoButton
              variant="secondary"
              // The check reads back the session this page's attempt created,
              // so it is offered only while this page still holds that
              // attempt's context.
              disabled={busy || !form.contextMatches}
              onClick={() => {
                void form.checkCreation();
              }}
            >
              Check again
            </RobotoButton>
            <ViewSessionLink sessionId={result.sessionId} />
          </>
        ),
      };
    }
    case "creationUnknown":
      return {
        notice: (
          <ResourceFormNotice
            tone="warning"
            role="alert"
            title="We couldn't confirm the session was created."
          >
            <p className={styles.resultLine}>
              Check your sessions before creating another.
            </p>
          </ResourceFormNotice>
        ),
        status: (
          <ResourceFormStatus>
            Open Sessions to check, or discard these results to start over.
          </ResourceFormStatus>
        ),
        buttons: (
          <>
            {discard}
            <RobotoButton
              variant="primary"
              disabled={busy}
              onClick={openSessions}
            >
              Open Sessions
            </RobotoButton>
          </>
        ),
      };
  }
}

function ViewSessionLink({ sessionId }: { sessionId: string }) {
  return (
    <ButtonLink variant="primary" href={Goto.createSessionRoute(sessionId)}>
      View session
    </ButtonLink>
  );
}

/** The files the attempt did not confirm in the session, in selection order. */
function unaddedEntries(
  result: SessionCreationResult,
): SessionCompositionEntry[] {
  return result.outcome.entries.filter((entry) => entry.status !== "included");
}

/**
 * Why the session may still exist, and what the last Check again found:
 * that it still does, or that the check itself failed.
 */
function cleanupUnknownTitle(
  result: Extract<SessionCreationResult, { kind: "cleanupUnknown" }>,
): string {
  if (result.survivingSession) {
    return "Not every file was added, and the session wasn't deleted.";
  }
  if (result.checkError !== undefined) {
    return "Not every file was added. We couldn't check whether the session was deleted.";
  }
  return "Not every file was added, and we couldn't confirm the session was deleted.";
}

/** Shown when the session's file list couldn't be read back after the files were sent. */
function ReconciliationNote({ result }: { result: SessionCreationResult }) {
  if (result.outcome.reconciliationError === undefined) {
    return null;
  }
  return (
    <p className={styles.resultLine}>
      We couldn’t read the session’s file list, so the unconfirmed files weren’t
      checked.
    </p>
  );
}

function fileCount(count: number): string {
  return count === 1 ? "1 file" : `${count} files`;
}

/**
 * Names the files a result is about by their path in the attempt's selection,
 * each with what is known about it. Past {@link MAX_INLINE_FILES} the names go
 * behind a disclosure so the notice stays short.
 */
function AffectedFiles({
  label,
  entries,
  files,
}: {
  label: string;
  entries: readonly SessionCompositionEntry[];
  files: readonly FileRecord[];
}) {
  if (entries.length === 0) {
    return null;
  }
  const pathById = new Map(
    files.map((file) => [file.file_id, file.relative_path]),
  );
  const name = (entry: SessionCompositionEntry) => (
    <>
      <code className={styles.fileName}>
        {pathById.get(entry.declaration.file_id) ?? entry.declaration.file_id}
      </code>
      {` (${sessionCompositionEntryReason(entry)})`}
    </>
  );
  if (entries.length <= MAX_INLINE_FILES) {
    return (
      <p className={styles.resultLine}>
        {label}:{" "}
        {entries.map((entry, index) => (
          <React.Fragment key={entry.originalIndex}>
            {index > 0 && ", "}
            {name(entry)}
          </React.Fragment>
        ))}
        .
      </p>
    );
  }
  return (
    <>
      <p className={styles.resultLine}>
        {label}: {fileCount(entries.length)}.
      </p>
      <details className={styles.fileDisclosure}>
        <summary>Show files</summary>
        <ul className={styles.fileList}>
          {entries.map((entry) => (
            <li key={entry.originalIndex}>{name(entry)}</li>
          ))}
        </ul>
      </details>
    </>
  );
}
